// lib/chat-context.tsx - COMPLETE FIXED VERSION (WEB SOCKET ONLY)
"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { authClient } from "@/lib/auth-client";
import { useWebSocket } from "@/lib/websocket-context";
import {
  createChat as createChatAction,
  getUserChats,
  getChatMessages,
  searchUsers as searchUsersAction,
} from "@/app/app/chat/actions";

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  org_name?: string;
  city?: string;
}

export interface ChatMessage {
  id: number;
  sender: string;
  title_id: number;
  text: string | null;
  attachements: string | null;
  attachements_type: number;
  sent_at: Date;
  deliverystatus: number;
  status: number | null;
  users: User;
}

export interface ChatRoom {
  id: number;
  name: string;
  type: "private" | "group";
  participants: User[];
  lastMessage?: ChatMessage;
  unreadCount: number;
  description?: string;
  isOnline?: boolean;
  lastUpdated?: Date;
}

interface ChatContextType {
  chatRooms: ChatRoom[];
  loading: boolean;
  error: string | null;
  selectedChat: ChatRoom | null;
  messages: ChatMessage[];
  onlineUsers: any[];
  isConnected: boolean;
  
  // Initial load - HTTP only once
  loadInitialChats: () => Promise<void>;
  
  // WebSocket actions
  sendMessage: (chatId: number, text: string) => Promise<void>;
  markAsRead: (chatId: number) => void;
  selectChat: (chat: ChatRoom | null) => void;
  
  // HTTP actions (only when WebSocket not available)
  createPrivateChat: (userIds: string[], title?: string) => Promise<{ success: boolean; chat?: ChatRoom }>;
  createGroupChat: (userIds: string[], title: string) => Promise<{ success: boolean; chat?: ChatRoom }>;
  searchUsers: (query: string) => Promise<User[]>;
  
  // Update chat list from WebSocket events
  updateChatFromWebSocket: (chatId: number, lastMessage?: ChatMessage) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Helper function to convert BigInt to string
const convertBigIntToString = (value: any): any => {
  if (typeof value === "bigint") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => convertBigIntToString(item));
  }

  if (value !== null && typeof value === "object") {
    const result: any = {};
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        result[key] = convertBigIntToString(value[key]);
      }
    }
    return result;
  }

  return value;
};

// Helper function to safely parse dates
const safeParseDate = (dateInput: any): Date => {
  try {
    if (!dateInput) return new Date();

    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? new Date() : dateInput;
    }

    const date = new Date(dateInput);
    return isNaN(date.getTime()) ? new Date() : date;
  } catch {
    return new Date();
  }
};

// Helper to format database message to ChatMessage
const formatDatabaseMessage = (msg: any): ChatMessage => {
  return {
    id: msg.id,
    sender: String(msg.sender || ""),
    title_id: Number(msg.title_id || 0),
    text: msg.text,
    attachements: msg.attachements,
    attachements_type: msg.attachements_type || 0,
    sent_at: safeParseDate(msg.sent_at),
    deliverystatus: msg.deliverystatus || 0,
    status: msg.status || 1,
    users: {
      id: String(msg.users?.id || msg.sender || ""),
      name: msg.users?.name || "Unknown",
      email: msg.users?.email || "",
      avatar: msg.users?.avatar,
    },
  };
};

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatRoom | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use WebSocket for real-time updates
  const {
    onlineUsers,
    sendMessage: sendWebSocketMessage,
    joinChat,
    leaveChat,
    addMessageListener,
    removeMessageListener,
    isConnected,
    addEventListener,
    removeEventListener,
  } = useWebSocket();

  // Use session only once, not for every update
  const { data: session } = authClient.useSession();

  // Refs
  const userId = useRef<string | null>(null);
  const messagesCache = useRef<Map<number, ChatMessage[]>>(new Map());
  const isMounted = useRef(true);
  const currentChatId = useRef<number | null>(null);
  const hasInitializedChats = useRef(false);
  const lastFetchTime = useRef<number>(0);
  const FETCH_COOLDOWN = 1000; // 1 second cooldown between fetches

  // Get current user ID once
  useEffect(() => {
    if (session?.user?.id) {
      const newUserId = session.user.id.toString();
      if (userId.current !== newUserId) {
        userId.current = newUserId;
        // Reset chat initialization when user changes
        hasInitializedChats.current = false;
      }
    }
  }, [session?.user?.id]);

  // Helper to format a single chat room
  const formatChatRoom = useCallback(
    (
      chat: any,
      currentUserId: string,
      onlineUserIds: Set<string>,
    ): ChatRoom => {
      const isGroup = chat.isgroup === 1;
      const lastMessage = chat.chats?.[0];

      // Process participants
      const participantMap = new Map<string, User>();
      chat.chat_members?.forEach((member: any) => {
        const user = member.users_chat_members_chat_withTousers;
        if (user) {
          participantMap.set(user.id.toString(), {
            id: user.id?.toString?.(),
            name: user.name,
            email: user.email,
            avatar: user.avatar,
            org_name: user.org_name,
            city: user.city,
          });
        }
      });

      const participants = Array.from(participantMap.values());

      // Determine chat name
      let chatName = chat.title || "";
      if (!isGroup && participants.length >= 2) {
        const otherUser = participants.find((p) => p.id !== currentUserId);
        chatName = otherUser?.name || "Private Chat";
      } else if (isGroup && !chatName) {
        chatName = participants
          .slice(0, 3)
          .map((p) => p.name)
          .join(", ");
        if (participants.length > 3)
          chatName += ` and ${participants.length - 3} more`;
      }

      // Check online status using provided onlineUserIds
      const isOnline = participants.some(
        (participant) =>
          participant.id !== currentUserId && onlineUserIds.has(participant.id),
      );

      // Format last message if exists
      let formattedLastMessage: ChatMessage | undefined;
      if (lastMessage) {
        formattedLastMessage = formatDatabaseMessage(lastMessage);
      }

      return {
        id: chat.id,
        name: chatName || "Unnamed Chat",
        type: isGroup ? "group" : "private",
        participants,
        lastMessage: formattedLastMessage,
        unreadCount: chat.unreadCount || 0,
        description: isGroup
          ? `${participants.length} members`
          : "Private conversation",
        isOnline,
        lastUpdated: chat.lastUpdated ? new Date(chat.lastUpdated) : new Date(),
      };
    },
    [],
  );

  // Load initial chats via HTTP (ONLY ONCE)
  const loadInitialChats = useCallback(async () => {
    // Prevent multiple simultaneous loads and enforce cooldown
    const now = Date.now();
    if (hasInitializedChats.current || loading || (now - lastFetchTime.current < FETCH_COOLDOWN)) {
      console.log("⏳ Skipping chat load - already loaded or in cooldown");
      return;
    }

    lastFetchTime.current = now;
    setLoading(true);
    setError(null);
    hasInitializedChats.current = true;

    try {
      const sessionData = await authClient.getSession();
      if (!sessionData?.data?.user?.id) {
        throw new Error("Unauthorized");
      }

      const currentUserId = sessionData.data.user.id.toString();
      userId.current = currentUserId;

      // Get online user IDs once for this fetch
      const onlineUserIds = new Set(onlineUsers.map((u) => u.userId));

      const userIdBigInt = BigInt(currentUserId);
      const result = await getUserChats(userIdBigInt);

      if (result.success && result.chats) {
        console.log(`📊 Got ${result.chats.length} chats from database (initial load)`);

        const safeResult = convertBigIntToString(result.chats);
        const formattedChats = safeResult.map((chat: any) =>
          formatChatRoom(chat, currentUserId, onlineUserIds),
        );

        // Sort chats by last message time or creation time
        formattedChats.sort((a, b) => {
          const timeA = a.lastMessage?.sent_at || a.lastUpdated || new Date(0);
          const timeB = b.lastMessage?.sent_at || b.lastUpdated || new Date(0);
          return timeB.getTime() - timeA.getTime();
        });

        if (isMounted.current) {
          setChatRooms(formattedChats);
        }
      } else {
        console.error("Failed to fetch chats:", result.error);
        if (isMounted.current) {
          setError(result.error || "Failed to fetch chats");
        }
      }
    } catch (err) {
      console.error("Error fetching chats:", err);
      if (isMounted.current) {
        setError("An error occurred while fetching chats");
      }
      // Reset so we can try again
      hasInitializedChats.current = false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [onlineUsers, formatChatRoom]);

  // Update chat from WebSocket event
  const updateChatFromWebSocket = useCallback((chatId: number, lastMessage?: ChatMessage) => {
    if (!isMounted.current) return;

    setChatRooms(prev => {
      const updated = prev.map(room => {
        if (room.id === chatId) {
          return {
            ...room,
            lastMessage,
            lastUpdated: new Date(),
          };
        }
        return room;
      });

      // Sort by last update time
      return updated.sort((a, b) => {
        const timeA = a.lastMessage?.sent_at || a.lastUpdated || new Date(0);
        const timeB = b.lastMessage?.sent_at || b.lastUpdated || new Date(0);
        return timeB.getTime() - timeA.getTime();
      });
    });
  }, []);

  // Listen for WebSocket chat updates
  useEffect(() => {
    if (!isConnected) return;

    const handleNewMessageFromWebSocket = (message: any) => {
      console.log("💬 Global WebSocket message received:", {
        id: message.id,
        chatId: message.title_id,
        sender: message.sender,
      });

      // Format the message
      const formattedMessage: ChatMessage = {
        id: message.id,
        sender: message.sender?.toString?.(),
        title_id: message.title_id,
        text: message.text,
        attachements: message.attachements,
        attachements_type: message.attachements_type || 0,
        sent_at: safeParseDate(message.sent_at),
        deliverystatus: message.deliverystatus || 0,
        status: message.status || 1,
        users: {
          id: message.users?.id?.toString?.() || message.sender?.toString?.(),
          name: message.users?.name || "Unknown",
          email: message.users?.email || "",
          avatar: message.users?.avatar,
        },
      };

      // Update the chat room that received the message
      updateChatFromWebSocket(message.title_id, formattedMessage);
    };

    const handleOnlineUsersUpdate = () => {
      // Update online status for all chats
      const onlineUserIds = new Set(onlineUsers.map((u) => u.userId));
      
      setChatRooms(prev => prev.map(room => {
        const isOnline = room.participants.some(
          (participant) =>
            participant.id !== userId.current &&
            onlineUserIds.has(participant.id),
        );
        return { ...room, isOnline };
      }));
    };

    // Listen for global new-message events
    addEventListener("new-message", handleNewMessageFromWebSocket);
    
    // Listen for online users updates
    const onlineUsersInterval = setInterval(handleOnlineUsersUpdate, 30000);

    return () => {
      removeEventListener("new-message", handleNewMessageFromWebSocket);
      clearInterval(onlineUsersInterval);
    };
  }, [isConnected, onlineUsers, addEventListener, removeEventListener, updateChatFromWebSocket]);

  // Listen for WebSocket messages for the SELECTED chat only
  useEffect(() => {
    if (!selectedChat) return;

    const handleNewMessageForSelectedChat = (message: any) => {
      if (!isMounted.current || !currentChatId.current) return;

      console.log("💬 Selected chat WebSocket message received:", {
        id: message.id,
        chatId: message.title_id,
        sender: message.sender,
      });

      // Format the WebSocket message
      const formattedMessage: ChatMessage = {
        id: message.id,
        sender: message.sender?.toString?.(),
        title_id: message.title_id,
        text: message.text,
        attachements: message.attachements,
        attachements_type: message.attachements_type || 0,
        sent_at: safeParseDate(message.sent_at),
        deliverystatus: message.deliverystatus || 0,
        status: message.status || 1,
        users: {
          id: message.users?.id?.toString?.() || message.sender?.toString?.(),
          name: message.users?.name || "Unknown",
          email: message.users?.email || "",
          avatar: message.users?.avatar,
        },
      };

      // Only process if it's for the selected chat
      if (formattedMessage.title_id === currentChatId.current) {
        console.log(`✅ Adding new message to selected chat ${currentChatId.current}`);

        setMessages((prev) => {
          // Add new message and sort by time
          const newMessages = [...prev, formattedMessage].sort((a, b) => {
            const timeA = a.sent_at.getTime();
            const timeB = b.sent_at.getTime();
            return timeA - timeB;
          });
          return newMessages;
        });

        // Update cache
        const cached = messagesCache.current.get(currentChatId.current) || [];
        messagesCache.current.set(currentChatId.current, [
          ...cached,
          formattedMessage,
        ]);
      }
    };

    console.log(`👂 Setting up WebSocket listener for selected chat ${selectedChat.id}`);
    addMessageListener(selectedChat.id, handleNewMessageForSelectedChat);

    return () => {
      console.log(`👋 Removing WebSocket listener for selected chat ${selectedChat.id}`);
      removeMessageListener(selectedChat.id);
    };
  }, [selectedChat, addMessageListener, removeMessageListener]);

  // Load messages for a chat - HTTP fallback only
  const loadChatMessages = useCallback(async (chatId: number): Promise<ChatMessage[]> => {
    try {
      console.log(`💾 Fetching messages from database for chat ${chatId}...`);
      const dbResult = await getChatMessages(chatId);

      if (dbResult.success && dbResult.messages && dbResult.messages.length > 0) {
        const formattedMessages = dbResult.messages.map((msg: any) =>
          formatDatabaseMessage(msg),
        );
        console.log(`✅ Loaded ${formattedMessages.length} messages from database for chat ${chatId}`);
        return formattedMessages;
      } else {
        console.log(`📭 No messages found in database for chat ${chatId}`);
        return [];
      }
    } catch (error) {
      console.error(`❌ Error loading messages for chat ${chatId}:`, error);
      return [];
    }
  }, []);

  // Mark chat as read
  const markAsRead = useCallback((chatId: number) => {
    if (!isMounted.current) return;
    
    console.log("✅ Marking chat as read:", chatId);
    setChatRooms((prev) =>
      prev.map((room) =>
        room.id === chatId ? { ...room, unreadCount: 0 } : room,
      ),
    );
  }, []);

  // Select a chat
  const selectChat = useCallback(
    async (chat: ChatRoom | null) => {
      if (!isMounted.current) return;
      
      console.log("🎯 Selecting chat:", chat?.id);

      // Leave previous chat if exists
      if (currentChatId.current) {
        console.log(`➖ Leaving previous chat: ${currentChatId.current}`);
        leaveChat(currentChatId.current);
      }

      setSelectedChat(chat);

      if (chat) {
        currentChatId.current = chat.id;
        setLoading(true);
        setError(null);

        try {
          // Join WebSocket room FIRST for real-time messages
          console.log(`➕ Joining WebSocket room for chat ${chat.id}`);
          joinChat(chat.id);

          // Load existing messages from cache or database
          const cachedMessages = messagesCache.current.get(chat.id);
          if (cachedMessages && cachedMessages.length > 0) {
            console.log(`✅ Using ${cachedMessages.length} cached messages for chat ${chat.id}`);
            setMessages(cachedMessages);
          } else {
            // Fallback to HTTP for initial message load
            console.log(`🔄 Loading messages from database for chat ${chat.id}...`);
            const loadedMessages = await loadChatMessages(chat.id);

            if (loadedMessages.length > 0) {
              // Sort by time
              const sortedMessages = loadedMessages.sort((a, b) => {
                const timeA = a.sent_at.getTime();
                const timeB = b.sent_at.getTime();
                return timeA - timeB;
              });

              console.log(`✅ Setting ${sortedMessages.length} messages for chat ${chat.id}`);
              setMessages(sortedMessages);
              messagesCache.current.set(chat.id, sortedMessages);
            } else {
              console.log(`📭 No messages found for chat ${chat.id}`);
              setMessages([]);
              messagesCache.current.set(chat.id, []);
            }
          }

          // Mark as read
          markAsRead(chat.id);
        } catch (error) {
          console.error(`❌ Error in selectChat for chat ${chat.id}:`, error);
          setError(`Failed to load messages for chat ${chat.id}`);
          setMessages([]);
        } finally {
          setLoading(false);
        }
      } else {
        currentChatId.current = null;
        setMessages([]);
      }
    },
    [leaveChat, joinChat, loadChatMessages, markAsRead],
  );

  // Send message via WebSocket
  const sendChatMessage = useCallback(
    async (chatId: number, text: string) => {
      let optimisticMessage: ChatMessage | null = null;

      try {
        const sessionData = await authClient.getSession();
        if (!sessionData?.data?.user?.id) {
          throw new Error("Unauthorized");
        }

        if (!isConnected) {
          throw new Error("WebSocket not connected");
        }

        // Create optimistic message
        optimisticMessage = {
          id: Date.now(), // Temporary ID
          sender: sessionData.data.user.id.toString(),
          title_id: chatId,
          text: text,
          attachements: null,
          attachements_type: 0,
          sent_at: new Date(),
          deliverystatus: 0,
          status: 1,
          users: {
            id: sessionData.data.user.id.toString(),
            name: sessionData.data.user.name || "You",
            email: sessionData.data.user.email || "",
            avatar: sessionData.data.user.avatar,
          },
        };

        console.log(`📤 Sending message to chat ${chatId}:`, text.substring(0, 50));

        // Add optimistic message immediately
        setMessages((prev) => {
          const newMessages = [...prev, optimisticMessage!].sort((a, b) => {
            const timeA = a.sent_at.getTime();
            const timeB = b.sent_at.getTime();
            return timeA - timeB;
          });
          return newMessages;
        });

        // Update cache
        const cached = messagesCache.current.get(chatId) || [];
        messagesCache.current.set(chatId, [...cached, optimisticMessage!]);

        // Update chat room with new message
        updateChatFromWebSocket(chatId, optimisticMessage);

        // Send via WebSocket
        await sendWebSocketMessage({
          chatId,
          text,
        });

        console.log(`✅ Message sent via WebSocket to chat ${chatId}`);
      } catch (err) {
        console.error("Error sending message:", err);
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred while sending message",
        );

        // Remove optimistic message on error
        if (optimisticMessage) {
          setMessages((prev) => {
            const filtered = prev.filter(
              (msg) => msg.id !== optimisticMessage!.id,
            );
            return filtered;
          });

          // Remove from cache
          const cached = messagesCache.current.get(chatId) || [];
          messagesCache.current.set(
            chatId,
            cached.filter((msg) => msg.id !== optimisticMessage!.id),
          );
        }
        throw err;
      }
    },
    [isConnected, sendWebSocketMessage, updateChatFromWebSocket],
  );

  // Create private chat (HTTP - only when needed)
  const createPrivateChat = useCallback(
    async (userIds: string[], title?: string) => {
      try {
        const sessionData = await authClient.getSession();
        if (!sessionData?.data?.user?.id) {
          throw new Error("Unauthorized");
        }

        if (userIds.length !== 1) {
          throw new Error("Private chat requires exactly one other user");
        }

        const currentUserId = sessionData.data.user.id.toString();
        const allUserIds = [currentUserId, ...userIds];

        const chatTitle = title || "Private Chat";
        const userIdsBigInt = allUserIds.map((id) => BigInt(id));

        const result = await createChatAction({
          title: chatTitle,
          isGroup: false,
          userIds: userIdsBigInt,
        });

        if (result.success) {
          // Reload chats
          hasInitializedChats.current = false;
          await loadInitialChats();
          return { success: true, chat: convertBigIntToString(result.chatTitle) };
        } else {
          setError(result.error || "Failed to create chat");
          throw new Error(result.error || "Failed to create chat");
        }
      } catch (err) {
        console.error("Error creating private chat:", err);
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred while creating chat",
        );
        throw err;
      }
    },
    [loadInitialChats],
  );

  // Create group chat (HTTP - only when needed)
  const createGroupChat = useCallback(
    async (userIds: string[], title: string) => {
      try {
        const sessionData = await authClient.getSession();
        if (!sessionData?.data?.user?.id) {
          throw new Error("Unauthorized");
        }

        const currentUserId = sessionData.data.user.id.toString();
        const allUserIds = [currentUserId, ...userIds];

        const userIdsBigInt = allUserIds.map((id) => BigInt(id));
        const result = await createChatAction({
          title,
          isGroup: true,
          userIds: userIdsBigInt,
        });

        if (result.success) {
          // Reload chats
          hasInitializedChats.current = false;
          await loadInitialChats();
          return { success: true, chat: convertBigIntToString(result.chatTitle) };
        } else {
          setError(result.error || "Failed to create group chat");
          throw new Error(result.error || "Failed to create group chat");
        }
      } catch (err) {
        console.error("Error creating group chat:", err);
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred while creating group chat",
        );
        throw err;
      }
    },
    [loadInitialChats],
  );

  // Search users (HTTP - only when needed)
  const searchUsers = useCallback(
    async (query: string): Promise<User[]> => {
      try {
        const sessionData = await authClient.getSession();
        if (!sessionData?.data?.user?.id) {
          return [];
        }

        const userIdBigInt = BigInt(sessionData.data.user.id);
        const result = await searchUsersAction(query, userIdBigInt);

        if (result.success && result.users) {
          return convertBigIntToString(result.users);
        }
        return [];
      } catch (err) {
        console.error("Error searching users:", err);
        return [];
      }
    },
    [],
  );

  // Initial load only when user ID is available
  useEffect(() => {
    if (!userId.current || hasInitializedChats.current) return;

    // Load chats once when user ID is available
    const timer = setTimeout(() => {
      loadInitialChats();
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [userId.current, loadInitialChats]);

  // Mount/unmount cleanup
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      
      // Leave current chat
      if (currentChatId.current) {
        try {
          leaveChat(currentChatId.current);
        } catch (error) {
          console.error("Error leaving chat on unmount:", error);
        }
      }
      
      // Clear cache
      messagesCache.current.clear();
    };
  }, [leaveChat]);

  // Memoized context value
  const contextValue = useMemo<ChatContextType>(
    () => ({
      chatRooms,
      loading,
      error,
      selectedChat,
      messages,
      onlineUsers,
      isConnected,
      loadInitialChats,
      sendMessage: sendChatMessage,
      markAsRead,
      selectChat,
      createPrivateChat,
      createGroupChat,
      searchUsers,
      updateChatFromWebSocket,
    }),
    [
      chatRooms,
      loading,
      error,
      selectedChat,
      messages,
      onlineUsers,
      isConnected,
      loadInitialChats,
      sendChatMessage,
      markAsRead,
      selectChat,
      createPrivateChat,
      createGroupChat,
      searchUsers,
      updateChatFromWebSocket,
    ],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}